/**
 * BigNumber tile — supports two shapes:
 *   • Single metric:  { value, prev, delta, label }                ← legacy
 *   • Multi-metric:   { metrics: [{label, value, prev, delta, agg, property}], label } ← new
 * When multi, renders the metrics in a single row (one column per metric) so a
 * card with Count + Sum + Unique reads horizontally like a scoreboard.
 */
import React from 'react';
import { Stat, StatNumber, StatHelpText, StatArrow, Text, SimpleGrid } from '@chakra-ui/react';

function formatCompact(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const x = Number(n);
  if (Math.abs(x) >= 1e9) return (x / 1e9).toFixed(2) + 'B';
  if (Math.abs(x) >= 1e6) return (x / 1e6).toFixed(2) + 'M';
  if (Math.abs(x) >= 1e3) return (x / 1e3).toFixed(1) + 'K';
  return Number.isInteger(x) ? x.toLocaleString() : x.toFixed(2);
}

function MetricBlock({ label, value, delta, dense }) {
  return (
    <Stat>
      <Text fontSize="xs" color="gray.600" mb={0.5}>{label}</Text>
      <StatNumber fontSize={dense ? '2xl' : '3xl'} fontWeight="bold" color="gray.800" lineHeight="1.1">
        {formatCompact(value)}
      </StatNumber>
      {delta != null && (
        <StatHelpText fontSize="xs" m={0} mt={0.5}>
          <StatArrow type={delta >= 0 ? 'increase' : 'decrease'} />
          {Math.abs(delta).toFixed(1)}% <Text as="span" color="gray.500">vs prev</Text>
        </StatHelpText>
      )}
    </Stat>
  );
}

export default function BigNumber({ data }) {
  if (!data) return null;
  const metrics = Array.isArray(data.metrics) ? data.metrics : null;

  // Multi-metric: one column per metric so values sit on a single row like a
  // scoreboard (Count · Sum · Unique). On narrow widths the grid auto-wraps.
  if (metrics && metrics.length > 1) {
    return (
      <SimpleGrid columns={{ base: 1, sm: metrics.length }} spacing={6}>
        {metrics.map((m, i) => (
          <MetricBlock key={i} label={m.label || `Metric ${i + 1}`} value={m.value} delta={m.delta} dense />
        ))}
      </SimpleGrid>
    );
  }

  // Single metric — render large.
  const m = metrics?.[0] || data;
  return <MetricBlock label={m.label || ''} value={m.value} delta={m.delta} />;
}
